import React, {useState, useEffect} from "react";
import {Dialog, DialogContent, DialogTitle, IconButton} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {useCharactersByPlayerRealtime} from "../hooks/useFirestore";
import {useAuth} from "../AuthContext";
import {checkCharacterLevelEligibility} from "../utils/levelUtils";
import {FirestoreSession} from "../services/firebase/firestore";

interface CharacterSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCharacterSelect: (characterId: string) => void;
  session?: FirestoreSession; // Add session prop for level checking
}

const CharacterSelectionModal: React.FC<CharacterSelectionModalProps> = ({
  isOpen,
  onClose,
  onCharacterSelect,
  session,
}) => {
  const {user} = useAuth();
  const {data: characters, loading} = useCharactersByPlayerRealtime(
    user?.uid || null
  );
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>("");

  // Filter active characters
  const activeCharacters = characters.filter(
    (char) => char.isActive && char.status === "active"
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedCharacterId("");
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    if (selectedCharacterId) {
      onCharacterSelect(selectedCharacterId);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedCharacterId("");
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Select Character
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="env-loading-state">
            <div className="loading-spinner"></div>
            <p>Loading characters...</p>
          </div>
        ) : activeCharacters.length === 0 ? (
          <div className="env-empty-state">
            <p>You don't have any active characters.</p>
            <p>Please create a character first before joining sessions.</p>
          </div>
        ) : (
          <>
            <p className="env-confirm-dialog-message">
              Choose which character you want to use for this session:
            </p>

            {session && (
              <p className="session-level-requirement">
                <strong>Session Level Requirement:</strong> {session.levelRange}
              </p>
            )}

            <div className="env-character-list">
              {activeCharacters.map((character) => {
                const characterLevel =
                  typeof character.level === "string"
                    ? parseInt(character.level, 10)
                    : character.level;

                // Check level eligibility if session is provided
                const levelCheck = session
                  ? checkCharacterLevelEligibility(
                      characterLevel,
                      session.levelRange
                    )
                  : {isEligible: true, warningType: null, warningMessage: null};

                return (
                  <div
                    key={character.id}
                    className={`env-character-item character-selection-item ${
                      selectedCharacterId === character.id
                        ? "character-selection-item-selected"
                        : ""
                    } ${!levelCheck.isEligible ? "character-level-warning" : ""}`}
                    onClick={() => setSelectedCharacterId(character.id || "")}
                  >
                    <div className="env-character-info">
                      <h3>{character.name}</h3>
                      <div className="env-character-class">
                        {character.species} {character.class} - Level{" "}
                        {characterLevel}
                      </div>

                      {/* Level warning message */}
                      {!levelCheck.isEligible && levelCheck.warningMessage && (
                        <div
                          className={`level-warning level-warning-${levelCheck.warningType}`}
                        >
                          <i className="warning-icon">⚠️</i>
                          <span className="warning-text">
                            {levelCheck.warningMessage}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="env-confirm-dialog-actions">
              <button onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!selectedCharacterId}
                className="btn btn-primary"
              >
                Join Session
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CharacterSelectionModal;
